import { plataforma } from '@/platform';
import {
  urlAssinaturaProduto,
  type EntitlementProduto,
} from '@/features/complementos/api/complementosApi';
import { Button } from '@/shared/ui/Button';
import { PageHeader } from '@/shared/ui/PageHeader';
import { IconLock } from '@/shared/ui/Icon';

type Props = {
  /** Código do produto no catálogo do servidor (`'pdv'`, …). */
  codigo: string;
  /** Nome exibido quando o servidor não mandou os dados do produto. */
  nomePadrao: string;
  /** Descrição exibida quando o servidor não mandou uma. */
  descricaoPadrao: string;
  entitlement: EntitlementProduto | null;
  carregando: boolean;
  erro: string | null;
  recarregar: (forcar?: boolean) => unknown;
};

const FMT_MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function sufixoFrequencia(meses: number): string {
  if (meses === 1) return '/mês';
  if (meses === 12) return '/ano';
  return ` a cada ${meses} meses`;
}

/**
 * Tela que ocupa o lugar de um recurso pago à parte quando a conta não
 * tem o produto. Mesmo desenho da trava do pós-venda: diz o que é, quanto
 * custa, leva para a compra no site e deixa conferir de novo depois de
 * pagar — sem precisar reiniciar o app.
 *
 * O link de compra é um `<a target="_blank">` comum: no navegador abre uma
 * aba nova; no desktop o main process intercepta (`setWindowOpenHandler`)
 * e entrega ao navegador padrão do sistema.
 */
export function BloqueioProduto({
  codigo,
  nomePadrao,
  descricaoPadrao,
  entitlement,
  carregando,
  erro,
  recarregar,
}: Props) {
  const produto = entitlement?.produto ?? null;
  const nome = produto?.nome || nomePadrao;
  const descricao = produto?.descricao || descricaoPadrao;
  const semConexao = entitlement?.origem === 'sem-conexao';

  const textoSemConexao = plataforma.capacidades.offline
    ? `Sem conexão com o servidor. Conecte à internet para confirmar o ${nome} — depois de confirmado, ele funciona sem internet por até 7 dias.`
    : 'Não foi possível confirmar o acesso agora — verifique sua conexão.';

  const rotuloVerificar = carregando
    ? 'Verificando…'
    : semConexao || (erro && !entitlement)
      ? 'Tentar novamente'
      : 'Já assinei — verificar novamente';

  return (
    <section className="page bloqueio-produto">
      <PageHeader titulo={nome} />
      <div className="card">
        <div className="card__body bloqueio-produto__corpo">
          <div className="bloqueio-produto__icone" aria-hidden="true">
            <IconLock width={40} height={40} />
          </div>
          <h3 className="bloqueio-produto__titulo">Recurso disponível como pacote à parte</h3>
          <p className="hint bloqueio-produto__texto">{descricao}</p>

          {produto && produto.precoBRL > 0 && (
            <p className="bloqueio-produto__preco" data-testid="preco-produto">
              {produto.precoOriginalBRL !== null && produto.precoOriginalBRL > produto.precoBRL && (
                <span className="bloqueio-produto__preco-original">
                  {FMT_MOEDA.format(produto.precoOriginalBRL)}
                </span>
              )}
              <strong>{FMT_MOEDA.format(produto.precoBRL)}</strong>
              <span className="bloqueio-produto__frequencia">
                {sufixoFrequencia(produto.frequenciaMeses)}
              </span>
            </p>
          )}

          {semConexao && (
            <p role="status" className="hint bloqueio-produto__texto">
              {textoSemConexao}
            </p>
          )}
          {erro && (
            <p role="alert" className="alert alert--error bloqueio-produto__erro">
              {erro}
            </p>
          )}

          <div className="bloqueio-produto__acoes">
            {!semConexao && (
              <a
                className="btn btn--primary"
                href={urlAssinaturaProduto(codigo)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Assinar no site
              </a>
            )}
            <Button onClick={() => void recarregar(true)} disabled={carregando}>
              {rotuloVerificar}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
